import { Anchor, Badge, Group, Loader, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../contexts/PermissionContext';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { SearchInput } from '../../../components/SearchInput';
import { UnifyTable, type UnifyTableColumn } from '../../../components/UnifyTable';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { CaseloadRowActionsMenu } from '../components/CaseloadRowActionsMenu';
import { CaseloadTabPanelLayout } from '../components/CaseloadTabPanelLayout';
import { TransferModal, type TransferModalContext } from '../components/TransferModal';
import {
  type Client,
  type ClientGroup,
  type EpisodeOfCareRow,
  episodeRowMatchesProviderFilter,
  formatCaseloadDate,
  formatEpisodeStatusLabel,
  getClientDisplayName,
  getEpisodeRows,
  getEpisodeStatusColor,
  defaultProviderFilterForRole,
  PROVIDER_FILTER_MY_CLIENTS,
  PROVIDER_FILTER_MY_TEAM,
  providerFilterOptionsFromRows,
  resolveAllByDefaultFilter,
  resolveFirstSessionDate,
} from '../data';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'planned', label: 'Planned' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'follow-up', label: 'Follow up' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'finished', label: 'Finished' },
];

const DEFAULT_STATUS_FILTER = ['active', 'planned', 'waitlist', 'follow-up', 'on-hold'];

interface ClientsTabProps {
  clients: Client[];
  clientGroups: ClientGroup[];
  loading?: boolean;
}

function stopRowActivation(e: React.KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
}

function firstSessionValue(row: EpisodeOfCareRow): string {
  return resolveFirstSessionDate(row.client.firstSession, row.episode.startDate);
}

function buildTransferContext(row: EpisodeOfCareRow): TransferModalContext {
  const client = row.client;
  const episodes = client.episodes ?? [];
  if (episodes.length === 0) {
    return {
      patientId: client.id,
      patientDisplay: row.displayName ?? getClientDisplayName(client),
      episodeOfCareId: row.episode.id,
      episodeDisplay: row.episode.name,
      serviceRequestId: '',
      currentProviderDisplay: row.episode.provider ?? client.primaryProvider ?? client.provider,
      serviceTypeDisplay: client.clientType,
      episodeOptions: [
        {
          value: row.episode.id,
          label: row.episode.name,
          currentProviderDisplay: row.episode.provider ?? client.primaryProvider ?? client.provider,
          serviceTypeDisplay: client.clientType,
        },
      ],
    };
  }

  const episodeOptions = episodes.map((episode) => ({
    value: episode.id,
    label: episode.name,
    currentProviderDisplay: episode.provider ?? client.primaryProvider ?? client.provider,
    serviceTypeDisplay: client.clientType,
  }));
  const defaultEpisode = episodes.find((episode) => episode.id === row.episode.id) ?? episodes[0];

  return {
    patientId: client.id,
    patientDisplay: row.displayName ?? getClientDisplayName(client),
    episodeOfCareId: defaultEpisode.id,
    episodeDisplay: defaultEpisode.name,
    serviceRequestId: '',
    currentProviderDisplay: defaultEpisode.provider ?? client.primaryProvider ?? client.provider,
    serviceTypeDisplay: client.clientType,
    episodeOptions,
  };
}

/**
 * Primary caseload table: one row per episode of care, with All-tab filters and row actions.
 */
export function ClientsTab({ clients, clientGroups, loading = false }: ClientsTabProps): JSX.Element {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const { practitioner: viewerPractitioner } = useCurrentUserPractitioner();

  const [searchQuery, setSearchQuery] = useState('');
  /** null = untouched. Resolved per render so the role default still applies once RBAC loads. */
  const [providerSelection, setProviderSelection] = useState<string[] | null>(null);
  const providerFilter = providerSelection ?? defaultProviderFilterForRole(role);
  /** null = untouched, so episode types that load later stay included. */
  const [episodeTypeSelection, setEpisodeTypeSelection] = useState<string[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>(DEFAULT_STATUS_FILTER);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferModalContext, setTransferModalContext] = useState<TransferModalContext | null>(null);

  const allEpisodeRows = useMemo(() => getEpisodeRows(clients, clientGroups), [clients, clientGroups]);

  const providerOptions = useMemo(
    () => [
      { value: PROVIDER_FILTER_MY_CLIENTS, label: 'My Clients' },
      { value: PROVIDER_FILTER_MY_TEAM, label: 'My Team' },
      ...providerFilterOptionsFromRows(allEpisodeRows, viewerPractitioner?.id),
    ],
    [allEpisodeRows, viewerPractitioner?.id],
  );

  const episodeTypeOptions = useMemo(() => {
    const names = new Set(allEpisodeRows.map((r) => r.episode.name));
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [allEpisodeRows]);

  const episodeTypeFilter = resolveAllByDefaultFilter(episodeTypeSelection, episodeTypeOptions);

  const filteredRows = useMemo(() => {
    let rows = allEpisodeRows;

    if (providerFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) =>
        episodeRowMatchesProviderFilter(r, providerFilter, {
          viewerPractitionerId: viewerPractitioner?.id,
        }),
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter((r) => {
        if (r.displayName?.toLowerCase().includes(query)) return true;
        const members = r.clients ?? [r.client];
        return members.some(
          (c) =>
            getClientDisplayName(c).toLowerCase().includes(query) ||
            c.email?.toLowerCase().includes(query) ||
            c.phone?.toLowerCase().includes(query),
        );
      });
    }

    if (episodeTypeFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) => episodeTypeFilter.includes(r.episode.name));
    }

    if (statusFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) => statusFilter.includes(r.episode.status));
    }

    return rows;
  }, [
    allEpisodeRows,
    providerFilter,
    viewerPractitioner?.id,
    searchQuery,
    episodeTypeFilter,
    statusFilter,
  ]);

  const handleTransfer = (row: EpisodeOfCareRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setTransferModalContext(buildTransferContext(row));
    setTransferModalOpen(true);
  };

  const columns = useMemo(
    (): UnifyTableColumn<EpisodeOfCareRow>[] => [
      {
        key: 'name',
        label: 'Client Name',
        sortable: true,
        getValue: (row) => row.displayName ?? getClientDisplayName(row.client),
        render: (row) => (
          <Group gap={6} wrap="nowrap">
            <Anchor
              component={Link}
              to={`/dashboard/clients/${row.client.id}`}
              size="sm"
              fw={500}
              c="gold.7"
              underline="hover"
              style={{ textUnderlineOffset: 2, maxWidth: 220 }}
              truncate="end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={stopRowActivation}
              title={row.displayName ?? getClientDisplayName(row.client)}
            >
              {row.displayName ?? getClientDisplayName(row.client)}
            </Anchor>
            {row.client.relationshipType === 'Supervisee' && (
              <Badge size="xs" variant="light" color="blue">
                Supervisee
              </Badge>
            )}
            {!row.clients &&
              ((row.client.highRiskFlags && row.client.highRiskFlags.length > 0) ||
                (row.client.riskTags && row.client.riskTags.length > 0)) && (
                <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
                  High Risk
                </Badge>
              )}
          </Group>
        ),
      },
      {
        key: 'episode',
        label: 'Episode of Care',
        sortable: true,
        getValue: (row) => row.episode.name,
        render: (row) => (
          <Anchor
            component={Link}
            to={`/dashboard/clients/${row.client.id}?tab=chart&episodeId=${encodeURIComponent(row.episode.id)}`}
            size="sm"
            c="gold.7"
            underline="hover"
            style={{ textUnderlineOffset: 2 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={stopRowActivation}
          >
            {row.episode.name}
          </Anchor>
        ),
      },
      {
        key: 'firstSession',
        label: 'First Session',
        sortable: true,
        getValue: (row) => firstSessionValue(row),
        render: (row) => <Text size="sm">{formatCaseloadDate(firstSessionValue(row))}</Text>,
      },
      {
        key: 'mostRecent',
        label: 'Most Recent Session',
        sortable: true,
        getValue: (row) => row.client.mostRecentAppointment ?? '',
        render: (row) => <Text size="sm">{formatCaseloadDate(row.client.mostRecentAppointment)}</Text>,
      },
      {
        key: 'clientType',
        label: 'Client Type',
        sortable: true,
        getValue: (row) => row.client.clientType,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        getValue: (row) => formatEpisodeStatusLabel(row.episode.status),
        render: (row) => (
          <Badge color={getEpisodeStatusColor(row.episode.status)} variant="light">
            {formatEpisodeStatusLabel(row.episode.status)}
          </Badge>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        pinRight: true,
        width: 48,
        render: (row) => (
          <CaseloadRowActionsMenu
            onTransfer={(e) => handleTransfer(row, e)}
          />
        ),
      },
    ],
    [],
  );

  if (loading && clients.length === 0) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading clients…
        </Text>
      </Group>
    );
  }

  return (
    <>
      <CaseloadTabPanelLayout
        filters={
          <>
            <SearchInput
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />
            <FilterMultiSelect
              allLabel="All Providers"
              data={providerOptions}
              value={providerFilter}
              onChange={setProviderSelection}
              selectedLabel="Selected"
              optionsAboveDivider={['my-clients', 'supervisee-caseload']}
            />
            <FilterMultiSelect
              allLabel="All Episodes"
              data={episodeTypeOptions}
              value={episodeTypeFilter}
              onChange={setEpisodeTypeSelection}
            />
            <FilterMultiSelect
              allLabel="All Statuses"
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </>
        }
      >
        <UnifyTable<EpisodeOfCareRow>
          withPaper={false}
          searchable={false}
          columns={columns}
          data={filteredRows}
          getRowKey={(row) => `${row.client.id}-${row.episode.id}`}
          sortable
          pagination
          defaultRowsPerPage={50}
          emptyMessage="No episodes of care found matching your criteria"
          onRowClick={(row) => navigate(`/dashboard/clients/${row.client.id}`)}
          tableProps={{ highlightOnHover: true }}
        />
      </CaseloadTabPanelLayout>

      <TransferModal
        opened={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setTransferModalContext(null);
        }}
        context={transferModalContext}
        onSuccess={() => {
          setTransferModalOpen(false);
          setTransferModalContext(null);
        }}
      />
    </>
  );
}
